import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useIsAuthenthicated } from './auth';
import { apiPaths, authHeader, handleResponse, queryClient } from './queryClient';
import {
  ApiClassroomSimulation,
  ApiExercise,
  ApiExerciseSummary,
  ApiInstructorExercise,
  ApiProfile,
  ApiSimulation,
  ApiSimulationSummary,
  ApiStudentSimulation,
  Assessment,
  Classroom,
  ExerciseFile,
  ExerciseMetaData,
  ExerciseStudent,
  ExerciseStudentDetails,
  ExerciseSummary,
  ExerciseType,
  Profile,
  SimulationStatus,
  Simulator,
  TrustCenterSection,
} from './types';
import { Collection } from './types/collection';
import { FAQData } from './types/faq';
import { parseTime } from './utils/parseTime';
import { useActiveCompany } from './utils/useCompany';

export const useSimulatorExercises = ({ simulatorId, onError }: { simulatorId?: string; onError?: () => void }) => {
  const companyId = useActiveCompany()?.companyId;
  const queryClient = useQueryClient();

  const queryKey = [{ path: `learn/companies/${companyId}/scheduled-exercises`, query: { simulator: simulatorId } }];

  const { data, isLoading, isError } = useQuery<ApiExerciseSummary[]>({
    enabled: !!(companyId && simulatorId),
    queryKey,
    onError() {
      onError && onError();
    },
  });

  const mappedExercises: ExerciseSummary[] | undefined = data?.map(parseTime);

  return {
    exercises: mappedExercises,
    loadingExercises: isLoading,
    exercisesError: isError,
    cancelExercises: () => {
      queryClient.cancelQueries({ queryKey });
    },
  };
};

export const useExercise = (exerciseId?: string) => {
  const companyId = useActiveCompany()?.companyId;

  const {
    data: exercise,
    isLoading: isLoadingExercise,
    error: exerciseError,
  } = useQuery<ApiExercise, Response>({
    queryKey: [{ path: `learn/companies/${companyId}/scheduled-exercises/${exerciseId}` }],
    enabled: !!companyId && !!exerciseId,
  });

  return { exercise, isLoadingExercise, exerciseError };
};

export const useInstructorExercise = (exerciseId?: string) => {
  const companyId = useActiveCompany()?.companyId;

  const {
    data: exercise,
    isLoading: isLoadingExercise,
    error: exerciseError,
  } = useQuery<ApiInstructorExercise, Response>({
    queryKey: [{ path: `teach/companies/${companyId}/scheduled-exercises/${exerciseId}` }],
    enabled: !!(companyId && exerciseId),
  });

  return { exercise, isLoadingExercise, exerciseError };
};

export const useExerciseStudents = (exerciseId?: string) => {
  const companyId = useActiveCompany()?.companyId;

  const {
    data: students,
    isLoading: isLoadingStudents,
    error: studentsError,
  } = useQuery<ExerciseStudent[], Response>({
    queryKey: [{ path: apiPaths.teach.users(companyId, exerciseId) }],
    enabled: !!(companyId && exerciseId),
  });

  return { students, isLoadingStudents, studentsError };
};

// Fetch simulations for an exercise for the current user
export const useSimulations = (exerciseId?: string) => {
  const companyId = useActiveCompany()?.companyId;

  const {
    data: simulations,
    isLoading: isLoadingSimulations,
    error: simulationsError,
  } = useQuery<ApiSimulationSummary[], Response>({
    enabled: !!companyId && !!exerciseId,
    queryKey: [{ path: `learn/companies/${companyId}/scheduled-exercises/${exerciseId}/simulations` }],
  });

  return { simulations, isLoadingSimulations, simulationsError };
};

// hasLiveUpdates: pass true when SignalR is already pushing live updates for this simulation
// elsewhere (e.g. via the active-simulation state), so we skip the redundant poll here.
export const useSimulation = (exerciseId?: string, simulationId?: string, hasLiveUpdates?: boolean) => {
  const activeCompany = useActiveCompany();

  const { data, error, isLoading } = useQuery<ApiSimulation, Response>({
    queryKey: [
      {
        path: apiPaths.learn.simulation(activeCompany?.companyId, exerciseId, simulationId),
      },
    ],
    enabled: !!(activeCompany && exerciseId && simulationId),
    refetchInterval: data => (data?.status === SimulationStatus.Ongoing && !hasLiveUpdates ? 5000 : false),
    refetchOnWindowFocus: true,
  });

  return {
    simulation: data,
    simulationError: error,
    simulationLoading: isLoading,
  };
};

export const useAssessments = (
  exerciseId?: string,
  simulationId?: string,
  platform?: number | string,
  assessmentVisibility?: boolean,
) => {
  const activeCompany = useActiveCompany();

  const { data, error, isLoading, fetchStatus } = useQuery<Assessment[], Response>({
    queryKey: [
      {
        path: apiPaths.learn.assessment(activeCompany?.companyId, simulationId, platform),
      },
    ],
    enabled: !!(activeCompany && exerciseId && simulationId && platform && assessmentVisibility),
  });

  return {
    assessments: data,
    assessmentsError: error,
    assessmentsLoading: isLoading && fetchStatus !== 'idle',
  };
};

export const useStudentAssessment = (
  exerciseId?: string,
  userId?: string,
  simulationId?: string,
  platform?: number | string,
) => {
  const activeCompany = useActiveCompany();

  const { data, error, isLoading } = useQuery<Assessment[], Response>({
    queryKey: [
      {
        path: apiPaths.teach.assessment(activeCompany?.companyId, userId, simulationId, platform),
      },
    ],
    enabled: !!(activeCompany && exerciseId && userId && simulationId && platform),
  });

  return {
    assessments: data,
    assessmentsError: error,
    assessmentsLoading: isLoading,
  };
};

const useClassRoomQueryKey = (exerciseId?: string) => {
  const activeCompany = useActiveCompany();
  return [
    {
      path: `teach/companies/${activeCompany?.companyId}/scheduled-exercises/${exerciseId}/classroom`,
    },
  ];
};

export const useStartClassroomSession = (exerciseId?: string, onSuccess?: () => void, onError?: () => void) => {
  const activeCompany = useActiveCompany();

  const mutation = useMutation<void, Response, void>({
    async mutationFn() {
      return fetch(`/api/teach/companies/${activeCompany?.companyId}/scheduled-exercises/${exerciseId}/classroom`, {
        headers: await authHeader(),
        method: 'POST',
      }).then(handleResponse);
    },
    onSuccess,
    onError,
  });

  return {
    ...mutation,
    sessionStarting: mutation.isLoading,
    startClassroomSession: mutation.mutate,
    startClassroomSessionError: mutation.error,
  };
};

export const useEndExercise = (
  onSuccess: (response: { message: string }) => void | Promise<void>,
  onError: () => void,
) => {
  const activeCompany = useActiveCompany();

  const mutation = useMutation<void, Response, string | undefined>({
    // @ts-ignore
    async mutationFn(exerciseId?: string) {
      return fetch(`/api/${apiPaths.teach.endExercise(activeCompany?.companyId, exerciseId)}`, {
        headers: await authHeader(),
        method: 'POST',
      }).then(handleResponse);
    },
    onSuccess,
    onError,
  });

  return {
    ...mutation,
    endExercise: mutation.mutate,
  };
};

export const useClassroom = (exerciseId?: string, enabled?: boolean) => {
  const activeCompany = useActiveCompany();
  const queryKey = useClassRoomQueryKey(exerciseId);

  const { data, error, isLoading } = useQuery<Classroom>({
    queryKey,
    enabled: enabled && !!(activeCompany && exerciseId),
  });

  return {
    classroom: data,
    classroomError: error,
    classroomLoading: isLoading,
  };
};

export const useUpdatePosition = exerciseId => {
  const queryClient = useQueryClient();
  const activeCompany = useActiveCompany();
  const queryKey = useClassRoomQueryKey(exerciseId);

  const mutation = useMutation<
    void,
    Response,
    { positionId: string; layoutId?: string; userId?: string },
    { previousClassroom: Classroom }
  >({
    // @ts-ignore
    async mutationFn() {
      // Get the new positions from the cache (they are optimistically set in onMutate)
      const classroom = queryClient.getQueryData<Classroom>(queryKey);
      if (!classroom) {
        return;
      }

      return fetch(
        `/api/teach/companies/${activeCompany?.companyId}/scheduled-exercises/${exerciseId}/classroom/positions`,
        {
          body: JSON.stringify(classroom.positions),
          headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
          method: 'PUT',
        },
      ).then(handleResponse);
    },
    onMutate({ positionId, layoutId, userId }) {
      const previousClassroom = queryClient.getQueryData<Classroom>(queryKey);

      queryClient.setQueryData<Classroom>(queryKey, classroom => {
        if (!classroom) {
          return;
        }

        return {
          ...classroom,
          positions: classroom.positions?.map(p => {
            return p.id === positionId
              ? { ...p, layoutId, userId }
              : { ...p, userId: p.userId === userId ? undefined : p.userId }; // User cannot be assigned to multiple positions
          }),
        };
      });

      return { previousClassroom };
    },
    onError(_, __, ctx) {
      queryClient.setQueryData(queryKey, ctx?.previousClassroom);
    },
  });

  return {
    updatePosition: mutation.mutate,
  };
};

export const useCancelClassroom = (exerciseId?: string, onSuccess?: () => void, onError?: () => void) => {
  const activeCompany = useActiveCompany();

  const mutation = useMutation<void, Response, void>({
    async mutationFn() {
      return fetch(`/api/teach/companies/${activeCompany?.companyId}/scheduled-exercises/${exerciseId}/classroom`, {
        headers: await authHeader(),
        method: 'DELETE',
      }).then(handleResponse);
    },
    onSuccess,
    onError,
  });

  return {
    ...mutation,
    cancelClassroom: mutation.mutate,
    isCancellingClassroom: mutation.isLoading,
    cancelClassroomError: mutation.error,
  };
};

export const useJoinClassroom = (classroomId?: string, onSuccess?: () => void) => {
  const activeCompany = useActiveCompany();

  const mutation = useMutation<void, Response, void>({
    async mutationFn() {
      return fetch(`/api/learn/companies/${activeCompany?.companyId}/classrooms/${classroomId}`, {
        headers: await authHeader(),
        method: 'POST',
      }).then(handleResponse);
    },
    onSuccess,
  });

  return {
    ...mutation,
    joinClassroom: mutation.mutate,
    isJoinLoading: mutation.isLoading,
    joinClassroomError: mutation.error,
  };
};

export const useJoinClassroomUsingPin = (onSuccess?: () => void, onError?: () => void) => {
  const mutation = useMutation<void, Response, string>({
    async mutationFn(classroomPin) {
      return fetch(`/api/classrooms/${classroomPin}`, {
        headers: await authHeader(),
        method: 'POST',
      }).then(handleResponse);
    },
    onSuccess,
    onError,
  });
  return {
    ...mutation,
    joinClassroom: mutation.mutate,
    isJoinLoading: mutation.isLoading,
    joinClassroomError: mutation.error,
  };
};

export const useLeaveClassroom = (classroomId?: string, onSuccess?: () => void) => {
  const activeCompany = useActiveCompany();

  const mutation = useMutation<void, Response, void>({
    async mutationFn() {
      return fetch(`/api/learn/companies/${activeCompany?.companyId}/classrooms/${classroomId}`, {
        headers: await authHeader(),
        method: 'DELETE',
      }).then(handleResponse);
    },
    onSuccess,
  });

  return {
    ...mutation,
    leaveClassroom: mutation.mutate,
    isLeaving: mutation.isLoading,
    leaveClassroomError: mutation.error,
  };
};

export const useClassroomSimulation = (exerciseId?: string, simulationId?: string) => {
  const activeCompany = useActiveCompany();

  const { data, error, isLoading } = useQuery<ApiClassroomSimulation, Response>({
    queryKey: [
      {
        path: apiPaths.teach.classroomSimulation(activeCompany?.companyId, exerciseId, simulationId),
      },
    ],
    enabled: !!(activeCompany && exerciseId && simulationId),
  });

  return {
    simulation: data,
    simulationError: error,
    simulationLoading: isLoading,
    updateClassroomSimulation(payload: any) {
      queryClient.setQueryData(
        [{ path: apiPaths.teach.classroomSimulation(activeCompany?.companyId, exerciseId, simulationId) }],
        payload,
      );
    },
  };
};

export const useUser = (exerciseId?: string, userId?: string) => {
  const activeCompany = useActiveCompany();

  const { data, error, isLoading } = useQuery<ExerciseStudentDetails>({
    queryKey: [
      {
        path: apiPaths.teach.user(activeCompany?.companyId, exerciseId, userId),
      },
    ],
    enabled: !!(activeCompany && exerciseId && userId),
  });

  return {
    user: data,
    userError: error,
    userLoading: isLoading,
  };
};

export const useStudentSimulation = (exerciseId?: string, userId?: string, simulationId?: string) => {
  const activeCompany = useActiveCompany();

  const { data, error, isLoading } = useQuery<ApiStudentSimulation>({
    queryKey: [
      {
        path: apiPaths.teach.simulation(activeCompany?.companyId, exerciseId, userId, simulationId),
      },
    ],
    enabled: !!(activeCompany && exerciseId),
  });

  return {
    simulation: data,
    simulationError: error,
    simulationLoading: isLoading,
  };
};

export const useSimulators = (exerciseType: ExerciseType) => {
  const activeCompany = useActiveCompany();

  const {
    data: simulators,
    isLoading: isSimulatorsLoading,
    isError: isSimulatorsError,
  } = useQuery<Simulator[]>({
    enabled: !!(activeCompany && exerciseType),
    queryKey: [
      { path: `teach/companies/${activeCompany?.companyId}/simulators`, query: { scheduleType: exerciseType } },
    ],
  });

  return { simulators, isSimulatorsLoading, isSimulatorsError };
};

export const useExerciseFiles = (simulatorId: string) => {
  const activeCompany = useActiveCompany();

  const {
    data: exercises,
    isLoading: isExercisesLoading,
    isError: isExercisesError,
  } = useQuery<ExerciseFile[]>({
    enabled: !!(activeCompany && simulatorId),
    queryKey: [{ path: `teach/companies/${activeCompany?.companyId}/exercises`, query: { simulator: simulatorId } }],
  });

  return { exercises, isExercisesLoading, isExercisesError };
};

export const useCollections = (simulatorId: string) => {
  const activeCompany = useActiveCompany();

  const {
    data: collections,
    isLoading: isCollectionsLoading,
    isError: isCollectionsError,
  } = useQuery<Collection[]>({
    enabled: !!(activeCompany && simulatorId),
    queryKey: [{ path: apiPaths.teach.exercises(activeCompany?.companyId), query: { simulator: simulatorId } }],
  });

  return { collections, isCollectionsLoading, isCollectionsError };
};

export const useMetaData = (onSuccess?: (response: ExerciseMetaData[]) => void, onError?: () => void) => {
  const activeCompany = useActiveCompany();

  const mutation = useMutation<ExerciseMetaData[], Response, string[]>({
    async mutationFn(ids: string[]) {
      return fetch(apiPaths.teach.metadata(activeCompany?.companyId), {
        headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
        method: 'POST',
        body: JSON.stringify(ids),
      }).then(handleResponse);
    },
    onSuccess,
    onError,
  });

  return {
    ...mutation,
    getMetaData: mutation.mutate,
    isExerciseMetaDataLoading: mutation.isLoading,
    isExerciseMetaDataError: mutation.error,
  };
};

export const useProfileNoScale = () => {
  const isAuthenticated = useIsAuthenthicated();

  const { data, isLoading, error } = useQuery<ApiProfile>({
    enabled: isAuthenticated,
    queryKey: [{ path: apiPaths.me, cache: 'no-cache' }],
  });

  const profile: Profile | undefined = data && { ...data, joinedDate: new Date(data.joinedDate) };

  return {
    profile,
    loadingProfile: isLoading,
    profileError: error,
  };
};

export const useProfile = () => {
  const isAuthenticated = useIsAuthenthicated();

  const { data, isLoading, error } = useQuery<ApiProfile>({
    enabled: isAuthenticated,
    queryKey: [{ path: apiPaths.me }],
    staleTime: 10000,
  });

  const profile: Profile | undefined = data && { ...data, joinedDate: new Date(data.joinedDate) };

  return {
    profile,
    loadingProfile: isLoading,
    profileError: error,
  };
};

export const useTrustCenterSections = () => {
  const {
    data: trustCenterSections,
    isLoading,
    error,
  } = useQuery<TrustCenterSection[]>({
    queryKey: [{ isPublic: true, path: 'trust-center' }],
    staleTime: 10000,
  });

  return {
    trustCenterSections,
    loadingSections: isLoading,
    sectionsError: error,
  };
};

export const useFAQ = () => {
  const { data, isLoading, error } = useQuery<FAQData[]>({
    queryKey: [{ isPublic: true, path: 'faq' }],
    staleTime: 10000,
  });

  return {
    data,
    isLoading,
    error,
  };
};
